/**
 * The typed piece form: which show style's piece types it offers (D32 — the
 * one the rundown will play in, `lib/editor/pieceTypes.ts`' order, and a
 * picker to choose another), a piece type picker over them, the chosen type's
 * params as fields built from its param specs (the operator screen's own
 * param control, `params/ParamControl.tsx`: enums, numbers, booleans, nested
 * objects, lists), each param committed alone as one leaf of `params` (so
 * concurrent edits of two params both win), the engine's verdict on the
 * whole (`checkParams`: what the production would refuse), and the piece's
 * timing (`enable`: start — a time or "now" — and duration).
 *
 * A param is committed when focus leaves its field (or on Enter in a text
 * field; a checkbox or a top-level picker at once), as it parses against its spec; a blank optional
 * param is written as null — absent, so the production applies its default.
 * A piece of a type the show style does not name keeps its type and shows its
 * raw params read-only, rather than losing them to a form that cannot hold
 * them; params the type does not name are shown beside the form and kept.
 */
import type { JsonValue } from '@acp/domain/primitives.js';
import { type FocusEvent, type KeyboardEvent, useState } from 'react';
import { formatDuration, parseDuration } from '../../lib/editor/duration.js';
import { type EditorPieceType, pieceParamsProblem, type ShowStyleSource, unnamedParams } from '../../lib/editor/pieceTypes.js';
import type { ParamSpec } from '@acp/engine/productionConfig.js';
import { type Draft, draftOf, type ParamField, paramFields, parseParams } from '../../lib/playout/paramForm.js';
import { ParamControl } from '../params/ParamControl.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { useFieldDraft } from './fieldDraft.js';

export type PieceTiming = { readonly start: number | 'now'; readonly duration: number | null };

/** The show styles the piece types can come from, and which one they do now (and why). */
export type ShowStylePick = {
  readonly options: readonly { readonly id: string; readonly name: string }[];
  readonly selectedId: string | null;
  readonly source: ShowStyleSource;
};

export type PieceFormProps = {
  /** The show style's piece types (empty while none applies, or it is loading). */
  readonly types: readonly EditorPieceType[];
  readonly showStyle: ShowStylePick;
  /** A show style picked here; null: back to automatic (the module's order). */
  readonly onShowStyleChange: (showStyleId: string | null) => void;
  readonly pieceType: string;
  /** The piece's params as the row holds them (`params.*`, expanded). */
  readonly params: Readonly<Record<string, JsonValue>>;
  /** The piece's timing (`enable`); null for an adlib, which has none until it is played. */
  readonly timing: PieceTiming | null;
  /** A new type was picked (the view also sets its source layer). */
  readonly onTypeChange: (type: EditorPieceType) => void;
  /** One param's new value; null clears it (absent: the spec's default applies). */
  readonly onParamCommit: (key: string, value: JsonValue) => void;
  readonly onTimingCommit: (timing: PieceTiming) => void;
  readonly onFocusField: (path: string | null) => void;
};

const SELECT_CLASS = 'h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50';

const SOURCE_TEXT: Readonly<Record<ShowStyleSource, string>> = {
  chosen: 'Chosen here (kept in this browser).',
  production: 'The show style a production of this rundown runs (fixed while it runs).',
  studio: 'The default show style of the rundown’s studio.',
  only: 'The only show style.',
  none: 'No show style applies to this rundown yet: pick the one it will play in.',
};

function DraftInput(props: { readonly id: string; readonly initial: string; readonly placeholder: string; readonly mono: boolean; readonly validate: (raw: string) => string | null; readonly onCommit: (raw: string) => void; readonly onFocus: () => void; readonly onBlur: () => void }) {
  const { id, initial, placeholder, mono, validate, onCommit, onFocus, onBlur } = props;
  const { draft, setDraft, dirty, markCommitted, rearm } = useFieldDraft(initial);
  const [error, setError] = useState<string | null>(null);
  const commit = () => {
    if (!dirty) return;
    const problem = validate(draft);
    setError(problem);
    if (problem !== null) return;
    markCommitted(draft);
    onCommit(draft);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  };
  return (
    <>
      <Input
        id={id}
        className={mono ? 'h-8 font-mono' : 'h-8'}
        value={draft}
        placeholder={placeholder}
        aria-invalid={error !== null}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => {
          rearm();
          onFocus();
        }}
        onBlur={() => {
          commit();
          onBlur();
        }}
        onKeyDown={onKeyDown}
      />
      {error === null ? null : (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

function startText(start: number | 'now'): string {
  return start === 'now' ? 'now' : formatDuration(start);
}

function Timing({ timing, onTimingCommit, onFocusField }: { readonly timing: PieceTiming; readonly onTimingCommit: (timing: PieceTiming) => void; readonly onFocusField: (path: string | null) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="grid gap-1">
        <Label htmlFor="piece-start" className="text-xs text-muted-foreground">
          Start
        </Label>
        <DraftInput
          id="piece-start"
          initial={startText(timing.start)}
          placeholder="0:00 or now"
          mono
          validate={(raw) => (raw.trim() === 'now' || typeof parseDuration(raw) === 'number' ? null : 'a time (m:ss) or “now”')}
          onCommit={(raw) => {
            const ms = parseDuration(raw);
            if (raw.trim() === 'now') onTimingCommit({ ...timing, start: 'now' });
            else if (typeof ms === 'number') onTimingCommit({ ...timing, start: ms });
          }}
          onFocus={() => onFocusField('enable')}
          onBlur={() => onFocusField(null)}
        />
      </div>
      <div className="grid gap-1">
        <Label htmlFor="piece-duration" className="text-xs text-muted-foreground">
          Duration
        </Label>
        <DraftInput
          id="piece-duration"
          initial={timing.duration === null ? '' : formatDuration(timing.duration)}
          placeholder="until ended"
          mono
          validate={(raw) => (parseDuration(raw) === undefined ? 'a duration (m:ss), or empty' : null)}
          onCommit={(raw) => {
            const ms = parseDuration(raw);
            if (ms !== undefined) onTimingCommit({ ...timing, duration: ms });
          }}
          onFocus={() => onFocusField('enable')}
          onBlur={() => onFocusField(null)}
        />
      </div>
    </div>
  );
}

/** A top-level param whose control is one choice — a checkbox, an enum's picker — commits as it changes. */
function committedAtOnce(spec: ParamSpec): boolean {
  if (spec.type === 'boolean') return true;
  if (spec.type === 'string') return spec.enum !== null;
  if (spec.type === 'number' || spec.type === 'integer') return spec.enum !== undefined;
  return false;
}

/**
 * One param, edited as a draft and committed on its own. The draft follows the row's value while it is clean
 * (someone else's edit lands in place); what the user is typing stays until it is committed (LWW then).
 */
function ParamFieldEditor({ field, value, onCommit, onFocusField }: { readonly field: ParamField; readonly value: JsonValue | undefined; readonly onCommit: (key: string, value: JsonValue) => void; readonly onFocusField: (path: string | null) => void }) {
  const stored = JSON.stringify(value ?? null);
  const [draft, setDraft] = useState<Draft>(field.initial);
  const [seen, setSeen] = useState(stored);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  if (stored !== seen) {
    setSeen(stored);
    if (!dirty) setDraft(draftOf(field.spec, value));
  }
  const commit = (next: Draft) => {
    const parsed = parseParams([field], { [field.name]: next });
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors({});
    setDirty(false);
    const out = parsed.params[field.name] ?? null;
    if (JSON.stringify(out) !== stored) onCommit(field.name, out);
  };
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    // Leaving the field (a list's add button or an object's next field is still inside it).
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    onFocusField(null);
    if (dirty) commit(draft);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || !(e.target instanceof HTMLInputElement) || e.target.type === 'checkbox') return;
    e.preventDefault();
    commit(draft);
  };
  return (
    <div className="grid gap-1" data-testid="piece-param" data-param={field.name} onFocus={() => onFocusField(`params.${field.name}`)} onBlur={onBlur} onKeyDown={onKeyDown}>
      <ParamControl
        idPrefix="param"
        path={field.name}
        label={field.label}
        spec={field.spec}
        draft={draft}
        errors={errors}
        onChange={(next) => {
          setDraft(next);
          // A checkbox or a picker is its own commit: there is nothing more to type.
          if (committedAtOnce(field.spec)) commit(next);
          else setDirty(true);
        }}
      />
    </div>
  );
}

/** The picker's "back to automatic" value: a space cannot be in an id. */
const AUTOMATIC = ' automatic';

function ShowStyleLine({ showStyle, onShowStyleChange, onFocusField }: { readonly showStyle: ShowStylePick; readonly onShowStyleChange: (id: string | null) => void; readonly onFocusField: (path: string | null) => void }) {
  return (
    <div className="grid gap-1" data-testid="piece-show-style" data-source={showStyle.source}>
      <Label htmlFor="piece-show-style" className="text-xs text-muted-foreground">
        Piece types from
      </Label>
      <select
        id="piece-show-style"
        className={SELECT_CLASS}
        value={showStyle.selectedId ?? ''}
        disabled={showStyle.source === 'production'}
        onFocus={() => onFocusField(null)}
        onChange={(e) => {
          if (e.target.value === AUTOMATIC) onShowStyleChange(null);
          else if (e.target.value !== '') onShowStyleChange(e.target.value);
        }}
      >
        {showStyle.selectedId === null ? <option value="">Choose a show style…</option> : null}
        {showStyle.source === 'chosen' ? <option value={AUTOMATIC}>Automatic (the rundown’s studio or production)</option> : null}
        {showStyle.options.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
      <p className={showStyle.source === 'none' ? 'text-xs font-medium text-foreground' : 'text-xs text-muted-foreground'}>{SOURCE_TEXT[showStyle.source]}</p>
    </div>
  );
}

function RawParams({ params, testId, label }: { readonly params: Readonly<Record<string, JsonValue>>; readonly testId: string; readonly label: string }) {
  return (
    <div className="grid gap-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <pre className="overflow-auto rounded-md bg-muted p-2 font-mono text-xs" data-testid={testId}>
        {JSON.stringify(params, null, 2)}
      </pre>
    </div>
  );
}

/** The form's parts for the piece's type: its param fields, the engine's verdict, the params it does not name. */
function typedParts(type: EditorPieceType | null, params: Readonly<Record<string, JsonValue>>) {
  if (type === null) return { fields: [], problem: null, unnamed: {} };
  return { fields: paramFields(type.config.params, params), problem: pieceParamsProblem(type, params), unnamed: unnamedParams(type, params) };
}

function TypedParams({ type, params, onParamCommit, onFocusField }: { readonly type: EditorPieceType; readonly params: Readonly<Record<string, JsonValue>>; readonly onParamCommit: (key: string, value: JsonValue) => void; readonly onFocusField: (path: string | null) => void }) {
  const { fields, problem, unnamed } = typedParts(type, params);
  return (
    <>
      {fields.map((field) => (
        // Another type is another form: its drafts start over.
        <ParamFieldEditor key={`${type.id}.${field.name}`} field={field} value={params[field.name]} onCommit={onParamCommit} onFocusField={onFocusField} />
      ))}
      {problem === null ? null : (
        <p className="rounded-md border border-warning bg-warning px-2 py-1 text-xs text-warning-foreground" role="status" data-testid="piece-params-problem">
          The production would refuse this piece: {problem}
        </p>
      )}
      {Object.keys(unnamed).length > 0 ? <RawParams params={unnamed} testId="unnamed-params" label="Params this type does not name (kept):" /> : null}
    </>
  );
}

export function PieceForm({ types, showStyle, onShowStyleChange, pieceType, params, timing, onTypeChange, onParamCommit, onTimingCommit, onFocusField }: PieceFormProps) {
  const type = types.find((t) => t.id === pieceType) ?? null;
  const rawParams = Object.entries(params);
  return (
    <section className="grid gap-3 border-t pt-3" data-testid="piece-form" aria-label="Piece">
      <ShowStyleLine showStyle={showStyle} onShowStyleChange={onShowStyleChange} onFocusField={onFocusField} />
      <div className="grid gap-1">
        <Label htmlFor="piece-type" className="text-xs text-muted-foreground">
          Piece type
        </Label>
        <select
          id="piece-type"
          className={SELECT_CLASS}
          value={type === null ? '' : type.id}
          onFocus={() => onFocusField('pieceType')}
          onBlur={() => onFocusField(null)}
          onChange={(e) => {
            const next = types.find((t) => t.id === e.target.value);
            if (next !== undefined) onTypeChange(next);
          }}
        >
          {type === null ? <option value="">{pieceType === '' ? 'Choose a type…' : `${pieceType} (not in this show style)`}</option> : null}
          {types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      {type === null ? null : <TypedParams type={type} params={params} onParamCommit={onParamCommit} onFocusField={onFocusField} />}
      {type === null && rawParams.length > 0 ? <RawParams params={params} testId="raw-params" label="Params (as stored):" /> : null}
      {timing === null ? null : <Timing timing={timing} onTimingCommit={onTimingCommit} onFocusField={onFocusField} />}
    </section>
  );
}
